<template>
  <div class="imprint-container">
    <div v-if="orderType === OrderType.SpecSample" style="display: flex; flex-direction: column;gap: 5px;">
      <div class="spec-sample-tips">
        <div style="margin: 10px">
          The cost presented is for 1 color, 1 location imprint. Additional colors may be available,
          and may incur additional fees. Please let us know in the notes if you would like to receive
          additional information.
        </div>
      </div>
    </div>
    <dl class="imprint-switch" v-if="blankLimitation === 3"
      v-show="isBlank || (selectedList.length > 0 && editIndex === 0)">
      <dt>Imprint Choice</dt>
      <dd>
        <button type="button" title="Imprint" :class="['btn-select', 'btn-lg', 'trans', isBlank ? '' : 'is-active']"
          @click="onSwitch(false)">
          Imprint
        </button>
        <button type="button" title="Blank" :class="['btn-select', 'btn-lg', 'trans', isBlank ? 'is-active' : '']"
          @click="onSwitch(true)">
          Blank
        </button>
      </dd>
    </dl>
    <div class="required-location" v-if="requiredLocation.length > 0" v-show="!isBlank">
      It is required to have imprints on the location {{ requiredLocationStr }} if you choose Imprint option.
    </div>
    <div class="blank-text" v-show="isBlank">Blank</div>
    <ul class="imprint-list" v-show="!isBlank">
      <li class="imprint-list__wrapper" v-for="(item, index) in selectedList" :key="item.objectId">
        <transition name="a-step-transition" v-on="useSlide">
          <div class="imprint-list__item">
            <imprint-detail :imprint-method-model="item" v-show="editIndex !== index" @edit="onDetailEdit(index)"
              @remove="onImprintRemove(item)" :key="item.objectId"></imprint-detail>

            <imprint-edit :current-index="index" :show-add-another-btn="isShowAddAnother" :imprint-method-model="item"
              @save="onUpdateModel" @remove="onImprintRemove(item)" @disable-location-click="onDisableLocationClick"
              @disable-method-click="onDisableMethodClick" @update:customerNotes="onUpdateCustomerNotesChanged"
              @update:persionalize="onUpdatePersionalize" v-show="editIndex === index"
              :key="item.objectId"></imprint-edit>
          </div>
        </transition>
      </li>
    </ul>
  </div>
  <div class="" v-show="isBlank || editIndex === -1">
    <div class="imprint-btn" v-show="!submitSatus">
      <ap-button type="primary" size="small" @click="onContinueClick">Continue</ap-button>

      <div v-show="isShowAddAnother">
        <span class="imprint-btn__or">or</span>
        <ap-button type="default" size="small" class="btn-add-another" @click="onAddAnotherLocation">Add Another
          Location</ap-button>
      </div>
    </div>
    <img src="https://img66.anypromo.com/frontimg/2023/Icon/loader.gif" alt="" class="submit-loading"
      v-show="submitSatus">
  </div>

  <teleport to="body">
    <div class="artwork_preview is-show" v-show="previewImage">
      <img :src="previewImage" alt="" />
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, inject, ref } from 'vue'
import { getMinQty, getTotalQty, useWindow, getPriceList, getMaxQty } from '../../composables'
import { CustomizeStep, type LocationGroup, type LocationMethod, OrderType } from '../../types'
import { SHOW_PRICE_GROUP_KEY } from '../../token'
import ImprintDetail from './imprintDetail.vue'
import ImprintEdit from './imprintEdit.vue'
import { useSlide } from '../../components/hooks/use-slide'
import { ApButton, ApMessageBox } from '@anypromo/components-pc'
import { useApp } from '../../store/modules/app'
import { useImprintMethod } from '../../store/modules/imprintMethod'
import { useProductSpec } from '../../store/modules/productSpec'
import { useAddon } from '../../store/modules/addon'
import { useSpec } from '../../store/modules/spec'
import { ImprintMethodModel } from '../../model'
import { useUpdateView } from '../../composables/use-update'

const props = withDefaults(
  defineProps<{
    submitSatus: boolean
  }>(),
  {
    submitSatus: false,
  }
)
const emit = defineEmits(['item-removed', 'go-next', 'go-back', 'switch-blank'])

const { locationGroupList, requiredLocation } = useWindow()

const appState = useApp()
const productSpecState = useProductSpec()
const specState = useSpec()
const addonState = useAddon()
const imprintMethodState = useImprintMethod()

const isBlank = computed(() => appState.getBlankValue)
const orderType = computed(() => appState.getOrderType)

const previewImage = ref('')

// 已选的imprint method
const selectedList = computed(() => imprintMethodState.getSelectedList)

const blankLimitation = computed(() => appState.getBlankLimitation)

// 当前编辑索引
const editIndex = computed(() => imprintMethodState.editIndex)

let requiredLocationStr: string = "";
if (requiredLocation.length > 1) {
  let comma = ''
  for (let index = 0; index < requiredLocation.length; index++) {
    const element = requiredLocation[index];
    requiredLocationStr += comma + `"${element}"`
    if (index < requiredLocation.length - 2) {
      comma = ", ";
    } else {
      comma = " and ";
    }
  }

} else if (requiredLocation.length === 1) {
  requiredLocationStr = `"${requiredLocation[0]}"`
}

// const requiredLocationStr = requiredLocation.map((f,i)=>i<requiredLocation.length -1 ? `"${f}"`).join(", ")

/**
 * 是否显示add another 按钮
 */
const isShowAddAnother = computed(() => {
  // 如果选择的是blank，那就不显示add another location按钮
  if (appState.getBlankValue) return false
  // 如果是Blank就不显示add another location按钮
  if (appState.getBlankLimitation === 2) return false
  // 如果是spec sample，就不显示按钮
  if (appState.getOrderType === OrderType.SpecSample) return false
  // 如果没有记录，那就不显示add location按钮
  if (selectedList.value.length === 0) return false

  if (selectedList.value.length >= appState.maxLocationNum) return false

  return selectedList.value.length < locationGroupList.filter((f) => f.Enabled).length
})

const showPriceGroup = inject(SHOW_PRICE_GROUP_KEY)
if (!showPriceGroup) {
  console.warn('build error')
}
const onContinueClick = () => {
  emit('go-next')
}

const onSwitch = (blankValue: boolean) => {
  if (blankValue === appState.getBlankValue) return

  const newBlankValue = !appState.getBlankValue
  const newPriceList = getPriceList({
    isBlank: newBlankValue,
    productSpecList: productSpecState.getSelectedList,
    specList: specState.getSelectedList,
    addonList: addonState.getSelectedList,
    imprintMethodList: newBlankValue ? [] : imprintMethodState.getSelectedList
  })

  // 如果当前是blank，
  if (appState.getBlankValue && newPriceList.length === 0) {
    // 如果没有可用price ,那说明不能切换，要给出提示。
    const _priceList = getPriceList({
      isBlank: newBlankValue,
      productSpecList: productSpecState.getSelectedList,
      specList: specState.getSelectedList,
      addonList: addonState.getSelectedList,
      imprintMethodList: []
    })

    if (_priceList.length > 0) {
      // appContextData!.currentPriceList = _priceList
      appState.setBlankValue(newBlankValue)
      appState.setCurrentPriceList(_priceList)
      imprintMethodState.empty()
      useUpdateView({
        isBlank: appState.getBlankValue,
        selectedProductSpecList: productSpecState.getSelectedList,
        selectedSpecList: specState.getSelectedList,
        selectedAddonList: addonState.getSelectedList,
        selectedImprintMethodList: imprintMethodState.getSelectedList,
        priceList: appState.getCurrentPriceList
      })
      // 创建新的
      imprintMethodState.create()
      return
    }
  }

  if (newPriceList.length === 0) {
    // 如果没有找到新的price ，说明当前是不支持的，需要弹出提示切换
    if (showPriceGroup) {
      showPriceGroup({
        type: 'switch',
        value: {
          name: '',
          value: ''
        }
      }).then((data) => {
        console.log('showPriceGroup')
      })
    }
    return
  } else {
    const minQty = Math.max(getMinQty(newPriceList, blankValue ? imprintMethodState.getSelectedList : []))
    // 获取当前qty
    const currentQty = getTotalQty(productSpecState.getSelectedList)

    if (currentQty < minQty) {
      ApMessageBox.confirm(
        `The minimum quantity for this item is ${minQty}. Please, update the quantity to proceed.`,
        'Confirm',
        {
          showClose: false,
          width: 'auto',
          confirmButtonText: 'Go to Step1',
          buttonCenter: true
        }
      ).then((data) => {
        // 切换到step1
        emit('go-back', CustomizeStep.ChooseProductSpec)
      })
      return
    }
    const maxQty = Math.min(getMaxQty(newPriceList))
    if (maxQty>0 && currentQty > maxQty) {
      ApMessageBox.confirm(
        `The maximum quantity for this item is ${maxQty}. Please, update the quantity to proceed.`,
        'Confirm',
        {
          showClose: false,
          width: 'auto',
          confirmButtonText: 'Go to Step1',
          buttonCenter: true
        }
      ).then((data) => {
        // 切换到step1
        emit('go-back', CustomizeStep.ChooseProductSpec)
      })
      return
    }
  }
  emit('switch-blank', blankValue)
}

const onAddAnotherLocation = () => {
  imprintMethodState.create()
  // emit("go-next");
}

const onDetailEdit = (index: number) => {
  if (editIndex.value !== -1) {
    ApMessageBox.alert('Please, Save the currently selected item before proceeding.', 'Info', {
      showClose: false
    })
    return
  }
  imprintMethodState.setEditIndex(index)
}
/**
 * 点击Remove按钮的事件
 * @param val
 */
const onImprintRemove = (val: ImprintMethodModel) => {
  const index = selectedList.value.indexOf(val)
  if (index !== -1) {
    if (selectedList.value.length === 1) {
      selectedList.value.splice(index, 1)
      imprintMethodState.create()
    } else if (editIndex.value !== -1) {
      const oldEditObj = selectedList.value[editIndex.value]
      selectedList.value.splice(index, 1)

      const newEditIndex = selectedList.value.findIndex(
        (f) => f.objectId === oldEditObj.objectId
      )
      imprintMethodState.setEditIndex(newEditIndex)
    } else {
      // 直接删除
      imprintMethodState.removeAt(index)
    }
  }
  emit('item-removed')
}


const onDisableLocationClick = (val: LocationGroup) => {
  // 需要切换
  if (showPriceGroup) {
    showPriceGroup({
      type: 'location',
      value: {
        name: val.Name,
        value: val.Name,
        location: val
      }
    }).then((data) => {
      console.log('showPriceGroup')
    })
  }
}

const onUpdateCustomerNotesChanged = (index: number, val: string) => {
  selectedList.value[index].decorationMethod.customerNotes = val
}
const onUpdatePersionalize = (index: number, val: boolean) => {
  selectedList.value[index].decorationMethod.isPersonalizationSelected = val
}

const onDisableMethodClick = (location: LocationGroup, method: LocationMethod) => {
  if (showPriceGroup) {
    showPriceGroup({
      type: 'method',
      value: {
        name: method.Location,
        value: method.Method,
        location: location,
        method: method
      }
    }).then((data) => {
      console.log('showPriceGroup')
    })
  }
}

/**
 * 更新Model
 * @param val
 * @param index
 */
const onUpdateModel = (index: number, isAddAnother: boolean) => {
  // 先查找对象
  imprintMethodState.setEditIndex(-1)

  if (isAddAnother) {
    //需要更新

    imprintMethodState.create()
  } else if (selectedList.value.length <= 1) {
    emit('go-next')
  }
}
</script>

<style lang="less">
.imprint-container {
  margin: 15px;

  .imprint-switch {
    margin-bottom: 17px;

    dd {
      padding-left: 20px;
    }

    .btn-select+.btn-select {
      margin-left: 3px;
    }
  }

  .imprint-radio-item {
    margin-bottom: 10px;
    font: normal 14px/18px;
    position: relative;
  }
}

.imprint-list {
  margin: 15px 0 0;
  padding: 0;
  margin-bottom: 10px;
  font: normal 14px/18px;
  position: relative;

  &__wrapper {
    margin: 0;
    padding: 0;
    list-style: none;
    border-radius: 3px;
  }

  &__item {
    margin-bottom: 10px;
  }
}

.blank-text {
  padding-left: 15px;
  display: inline-block;
  font-size: 13px;
}

.imprint-btn {
  margin-top: 10px;
  padding-left: 15px;
  margin-bottom: 15px;
  display: flex;

  &__or {
    margin-left: 10px;
    padding: 3px 15px 0px 5px;
  }
}

.btn-add-another {
  color: #fff;
  background: #fbbc05;
  border-color: transparent;

  &:hover {
    color: #fff;
    background: #f9cd4c;
  }
}

.artwork_preview {
  width: 106px;
  height: 106px;

  &.is-show {
    background-color: #fff;
    border: 1px solid #696969;
    z-index: 999;
    display: block;
    position: absolute;
  }

  img {
    height: 94px;
    width: 94px;
    margin: 6px;
    border: none;
  }
}

.required-location {
  background-color: #FAFAFA;
  border: 1px solid #bbd3de;
  border-radius: 3px;
  font-size: 13px;
  padding: 10px;
  color: red;
}
</style>
